import React, { useState, useEffect, useRef } from 'react';
import { RobuxIcon, WalletValue, useAuth, walletAmount } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { Users, History, Rocket, ShieldCheck, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { useChatLayout } from '../context/ChatLayoutContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const WS_URL = BACKEND_URL.replace(/^http/, 'ws');

// ==========================================
// YENİ: K, M, B FORMATLAYICILARI (ÇİFT YÖNLÜ)
// ==========================================
const parseAmount = (valStr) => {
  if (!valStr) return 0;
  // ⚠️ BİNLİK AYIRICI TEMİZLENMELİ. formatAmount() büyük sayıları
  // toLocaleString ile üretiyor ("1,457,545"); parseFloat virgülde durup
  // 1 döndürüyordu. MAX butonuna basan kullanıcı 1 birim bahis oynuyordu.
  let str = valStr.toString().trim().toLowerCase().replace(/[,\s]/g, '');
  let multiplier = 1;
  if (str.endsWith('k')) { multiplier = 1000; str = str.slice(0, -1); }
  else if (str.endsWith('m')) { multiplier = 1000000; str = str.slice(0, -1); }
  else if (str.endsWith('b')) { multiplier = 1000000000; str = str.slice(0, -1); }
  
  const parsedValue = parseFloat(str);
  return isNaN(parsedValue) ? 0 : Math.floor(parsedValue * multiplier);
};

const formatAmount = (amount) => {
  if (amount === undefined || amount === null || amount === 0) return '';
  if (amount >= 1000000) return amount.toLocaleString();
  if (amount >= 1000) return parseFloat((amount / 1000).toFixed(2)) + 'K';
  return amount.toString();
};

const niceTicks = (ymax) => {
  const rng = ymax - 1, raw = rng / 4;
  const mag = Math.pow(10, Math.floor(Math.log10(raw || 1)));
  let step = mag;
  for (const mu of [1, 2, 2.5, 5, 10]) { step = mu * mag; if (raw / step <= 1.2) break; }
  const t = [];
  for (let v = 1; v <= ymax + 1e-9; v += step) t.push(Math.round(v * 100) / 100);
  return t;
};

// ── Profesyonel casino crash grafiği: ızgara + çarpan ekseni + parlak eğri (gloss + komet + glow) + vinyet ──
const CrashGraph = ({ multiplier, phase }) => {
  const W = 1000, H = 600, pL = 78, pR = 36, pT = 44, pB = 58;
  const x0 = pL, x1 = W - pR, y0 = pT, y1 = H - pB, pw = x1 - x0, ph = y1 - y0;
  const crashed = phase === 'crashed';
  const showCurve = phase === 'playing' || phase === 'crashed';
  const col = crashed ? '#FF4D63' : '#22E58A';
  const m = Math.max(1, multiplier || 1);
  const ymax = Math.max(2, m * 1.18);
  const lnm = Math.max(Math.log(m), 0.06);
  const X = (v) => x0 + pw * (0.9 * Math.log(Math.max(v, 1)) / lnm);
  const Y = (v) => y1 - ph * ((v - 1) / (ymax - 1));
  const N = 72;
  const pts = Array.from({ length: N + 1 }, (_, i) => { const v = 1 + (m - 1) * i / N; return [X(v), Y(v)]; });
  const curve = 'M ' + pts.map(([x, y]) => `${x.toFixed(1)} ${y.toFixed(1)}`).join(' L ');
  const tip = pts[pts.length - 1];
  const area = `${curve} L ${tip[0].toFixed(1)} ${y1} L ${x0} ${y1} Z`;
  const kk = Math.max(1, Math.round(N * 0.12));
  const trail = 'M ' + pts.slice(-kk).map(([x, y]) => `${x.toFixed(1)} ${y.toFixed(1)}`).join(' L ');
  const ticks = niceTicks(ymax);
  const vlines = [1, 2, 3, 4, 5].map((i) => x0 + pw * i / 6);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="absolute inset-0 w-full h-full z-0">
      <defs>
        <linearGradient id="cg_bg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#151920" /><stop offset="100%" stopColor="#0c0e12" />
        </linearGradient>
        <linearGradient id="cg_area" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={col} stopOpacity="0.32" /><stop offset="100%" stopColor={col} stopOpacity="0.015" />
        </linearGradient>
        <radialGradient id="cg_vig" cx="50%" cy="42%" r="75%">
          <stop offset="60%" stopColor="#000" stopOpacity="0" /><stop offset="100%" stopColor="#000" stopOpacity="0.35" />
        </radialGradient>
        <filter id="cg_glow" x="-80%" y="-80%" width="260%" height="260%"><feGaussianBlur stdDeviation="6" /></filter>
      </defs>

      <rect width={W} height={H} fill="url(#cg_bg)" />

      {ticks.map((v, i) => {
        const yy = Y(v);
        return (
          <g key={`t${i}`}>
            <line x1={x0} y1={yy} x2={x1} y2={yy} stroke="#242a33" strokeWidth="1" strokeDasharray="1 9" />
            <text x={x0 - 14} y={yy + 6} fill="#59626f" fontSize="19" fontFamily="monospace" fontWeight="bold" textAnchor="end">{v}x</text>
          </g>
        );
      })}
      {vlines.map((xx, i) => (
        <line key={`v${i}`} x1={xx} y1={y0} x2={xx} y2={y1} stroke="#1b1f27" strokeWidth="1" strokeDasharray="1 9" />
      ))}
      <line x1={x0} y1={y1} x2={x1} y2={y1} stroke="#2c333d" strokeWidth="2" />

      {showCurve && (
        <g>
          <path d={area} fill="url(#cg_area)" />
          <path d={curve} fill="none" stroke={col} strokeWidth="9" opacity="0.35" strokeLinejoin="round" strokeLinecap="round" filter="url(#cg_glow)" />
          <path d={curve} fill="none" stroke={col} strokeWidth="4.5" strokeLinejoin="round" strokeLinecap="round" />
          <path d={curve} fill="none" stroke="#ffffff" strokeWidth="1.4" opacity="0.55" strokeLinejoin="round" strokeLinecap="round" />
          <path d={trail} fill="none" stroke="#ffffff" strokeWidth="5" opacity="0.5" strokeLinecap="round" filter="url(#cg_glow)" />
          <circle cx={tip[0]} cy={tip[1]} r="22" fill={col} opacity="0.22" filter="url(#cg_glow)" />
          <circle cx={tip[0]} cy={tip[1]} r="8.5" fill="#ffffff" />
          <circle cx={tip[0]} cy={tip[1]} r="4" fill={col} />
        </g>
      )}

      <rect width={W} height={H} fill="url(#cg_vig)" />
    </svg>
  );
};

export const Crash = () => {
  const { user, token, isAuthenticated, balance, fmtMoney, wallet, betToRaw, rawToBet, betLimits } = useAuth();
  const wsRef = useRef(null);
  
  // ==========================================
  // SES EFEKTLERİ (Audio Refs)
  // ==========================================
  const fuseSound = useRef(null);
  const explosionSound = useRef(null);

  useEffect(() => {
    if (typeof Audio !== "undefined") {
      fuseSound.current = new Audio('/fuse.mp3');
      explosionSound.current = new Audio('/explosion.mp3');
      
      fuseSound.current.preload = 'auto';
      explosionSound.current.preload = 'auto';
    }
    
    return () => {
      if (fuseSound.current) {
        fuseSound.current.pause();
        fuseSound.current.src = ""; 
      }
      if (explosionSound.current) {
        explosionSound.current.pause();
        explosionSound.current.src = ""; 
      }
    };
  }, []); 

  const [betAmount, setBetAmount] = useState('');
  // 💬 Aç/kapa durumu paylaşımlı: sayfa değişse de korunur.
  const { chatOpen } = useChatLayout();
  const [autoCashout, setAutoCashout] = useState('2.00');
  
  const [phase, setPhase] = useState('betting'); 
  const [timeRemaining, setTimeRemaining] = useState(10.0); 
  const [multiplier, setMultiplier] = useState(1.00);
  const [serverHash, setServerHash] = useState(''); 
  const [history, setHistory] = useState([]);
  const [players, setPlayers] = useState([]); 

  const myBet = players.find(p => p.user_id === user?.id);

  const getMultiplierColor = (val) => {
    if (val < 2.0) return 'text-rose-500 bg-rose-500/10 border-rose-500/30';
    if (val < 10.0) return 'text-[#2ecf6e] bg-[#16A34A]/10 border-[#16A34A]/30';
    return 'text-[#c98bf0] bg-[#B059E8]/10 border-[#B059E8]/30';
  };

  const getMultiplierTextOnly = (val) => {
    if (val < 2.0) return 'text-rose-500';
    if (val < 10.0) return 'text-[#2ecf6e]';
    return 'text-[#c98bf0]';
  };

  // ==========================================
  // SES YÖNETİCİSİ (Aşamalara Göre Ses Çalma)
  // ==========================================
  useEffect(() => {
    if (!fuseSound.current || !explosionSound.current) return;

    fuseSound.current.loop = false;
    fuseSound.current.volume = 0.5; 
    explosionSound.current.volume = 0.8; 

    const playPromise = (audioRef) => {
        audioRef.current.currentTime = 0;
        const promise = audioRef.current.play();
        if (promise !== undefined) {
            promise.catch(e => console.log("Browser blocked autoplay."));
        }
    };

    if (phase === 'playing') {
      playPromise(fuseSound);
    } 
    else if (phase === 'crashed') {
      fuseSound.current.pause();
      playPromise(explosionSound);
    } 
    else if (phase === 'betting') {
      fuseSound.current.pause();
      explosionSound.current.pause();
    }

    return () => {
        fuseSound.current?.pause();
        explosionSound.current?.pause();
    };
  }, [phase]);

  // ==========================================
  // WEBSOCKET VE DİĞER FONKSİYONLAR
  // ==========================================
  useEffect(() => {
    const wsToken = token || 'guest';

    const ws = new WebSocket(`${WS_URL}/ws/crash/${wsToken}`);
    wsRef.current = ws;

    ws.onopen = () => console.log('🚀 Connected to Crash Server!');

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'CRASH_STATE') {
          setPhase(data.phase);
          setTimeRemaining(data.time);
          if (data.multiplier) setMultiplier(data.multiplier);
          
          if (data.phase === 'betting') setMultiplier(1.00); 
          
          if (data.server_seed_hash) setServerHash(data.server_seed_hash);
          if (data.history) setHistory(data.history);
          if (data.bets) setPlayers(data.bets.sort((a, b) => b.amount - a.amount));
        }
        else if (data.type === 'CRASH_START') {
          setPhase('playing');
          setMultiplier(1.00);
        }
        else if (data.type === 'CRASH_TICK') {
          setPhase('playing'); 
          setMultiplier(data.multiplier);
        }
        else if (data.type === 'NEW_BET') {
          setPlayers(prev => [...prev, data.bet].sort((a, b) => b.amount - a.amount));
        }
        else if (data.type === 'PLAYER_CASHED_OUT') {
          setPlayers(prev => prev.map(p => 
            p.user_id === data.user_id 
              ? { ...p, status: 'cashed_out', cashout_at: data.cashout_at } 
              : p
          ));
        }
        else if (data.type === 'CRASH_CRASHED') {
          setPhase('crashed');
          setMultiplier(data.multiplier);
          setPlayers(prev => prev.map(p => 
            p.status === 'playing' ? { ...p, status: 'crashed' } : p
          ));
          setHistory(prev => [data.multiplier, ...prev].slice(0, 10));
        }
        else if (data.type === 'ERROR') toast.error(data.message);

      } catch (error) {
        console.error("WS Parse Error:", error);
      }
    };

    return () => ws.close();
  }, [token]);

  // YENİ: K, M, B Destekli Hızlı Buton Fonksiyonları
  const handleBetChange = (val) => setBetAmount(val);
  
  const multiplyBet = (mult) => {
    const current = parseAmount(betAmount);
    setBetAmount(String(Math.floor(current * mult)));
  };

  const addBetAmount = (val) => {
    const current = parseAmount(betAmount);
    setBetAmount(String(current + val));
  };

  const placeBet = () => {
    if (!isAuthenticated) return toast.error("Please log in first to place a bet!");
    
    // 🎯 Kullanıcı AKTİF CÜZDANIN biriminde yazar (Robux → site birimi,
    // BB Coin → BB). betToRaw saklama birimine çevirir: BB'de ×1000.
    // Bu satır olmadan "10" yazan oyuncu 10 mili-BB (0,01 BB) oynardı.
    const amount = betToRaw(parseAmount(betAmount)); // YENİ: Harfli metni saf sayıya çeviriyoruz
    const targetCashout = parseFloat(autoCashout);

    // ⚠️ Limitler cüzdana göre; "1 – 100.000" SİTE birimidir ve
    // BB Coin bahislerini haksız yere reddederdi.
    if (!amount || amount < betLimits.min_bet || amount > betLimits.max_bet) {
      return toast.error(`Bet must be between ${walletAmount(betLimits.min_bet, wallet)} and ${walletAmount(betLimits.max_bet, wallet)}.`);
    }
    if (targetCashout && targetCashout < 1.01) {
      return toast.error("Auto cashout must be at least 1.01x!");
    }
    // 🍀 AKTİF CÜZDANIN bakiyesiyle karşılaştır (LC'de mili-LC).
    if (amount > balance) return toast.error("Insufficient balance!");
    if (phase !== 'betting') return toast.error("You cannot place a bet right now!");
    if (myBet) return toast.error("You already placed a bet!");

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'PLACE_BET',
        amount: amount,
        auto_cashout: targetCashout || 2.0 
      }));
    } else {
      toast.error("Server connection lost!");
    }
  };

  const handleCashout = () => {
    if (phase !== 'playing' || !myBet || myBet.status !== 'playing') return;
    
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'CASHOUT' }));
    }
  };

  return (
    <div className="min-h-screen bg-[#0E0F11] pt-24 pb-8" data-testid="crash-page">
      <div className="max-w-[1800px] mx-auto px-4">
        <div className={`flex flex-col lg:flex-row gap-6 transition-[padding] duration-300 ${chatOpen ? 'md:pl-[340px] lg:pl-[370px]' : ''}`}>
          
          {/* 💬 Sohbet App.js'te TEK kopya render ediliyor (ChatDock).
              Sayfa-içi kopya kaldırıldı: her gezinmede WebSocket kopup
              yeniden bağlanıyor ve 4 HTTP isteği tekrar atılıyordu. */}
          <main className="flex-1 flex flex-col md:grid md:grid-cols-[320px_1fr] md:grid-rows-[auto_1fr] gap-6 min-w-0">
            
            {/* 1. KONTROLLER */}
            <div className="order-1 md:order-none md:col-start-1 md:row-start-1 flex flex-col">
              <div className="bg-[#1A1D20] rounded-2xl border border-[#2A2D31] p-4 shadow-lg">
                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-zinc-400 font-bold mb-1.5 block">Bet Amount</label>
                    <div className="flex bg-[#15171a] border border-[#2A2D31] rounded-xl overflow-hidden focus-within:border-[#B059E8] transition-colors">
                      <div className="pl-3 flex items-center justify-center">
                        <span className="text-zinc-500 font-bold">$</span>
                      </div>
                      <input
                        type="text" // YENİ: Type "number" yerine "text" oldu ki harf yazılabilsin
                        value={betAmount}
                        onChange={(e) => handleBetChange(e.target.value)}
                        disabled={phase !== 'betting' && !myBet}
                        className="w-full bg-transparent py-3 px-2 text-white font-bold outline-none disabled:opacity-50 uppercase"
                        placeholder="1"
                      />
                      <div className="flex border-l border-[#2A2D31]">
                        <button onClick={() => addBetAmount(10)} disabled={phase !== 'betting' && !myBet} className="px-3 text-xs font-bold text-zinc-400 hover:text-white hover:bg-[#33363b] transition-colors border-r border-[#2A2D31]">+10</button>
                        <button onClick={() => multiplyBet(0.5)} className="px-3 text-xs font-bold text-zinc-400 hover:text-white hover:bg-[#33363b] transition-colors border-r border-[#2A2D31]">1/2</button>
                        <button onClick={() => multiplyBet(2)} className="px-3 text-xs font-bold text-zinc-400 hover:text-white hover:bg-[#33363b] transition-colors">2x</button>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs text-zinc-400 font-bold mb-1.5 block">Auto Cashout</label>
                    <div className="flex bg-[#15171a] border border-[#2A2D31] rounded-xl overflow-hidden focus-within:border-[#B059E8] transition-colors">
                      <div className="pl-3 flex items-center justify-center">
                        <span className="text-zinc-500 font-bold">x</span>
                      </div>
                      <input
                        type="number"
                        step="0.01"
                        value={autoCashout}
                        onChange={(e) => setAutoCashout(e.target.value)}
                        disabled={phase !== 'betting' && !myBet}
                        className="w-full bg-transparent py-3 px-2 text-white font-bold outline-none disabled:opacity-50"
                        placeholder="2.00"
                      />
                    </div>
                  </div>

                  {!myBet ? (
                    <button 
                      onClick={placeBet}
                      disabled={phase !== 'betting'}
                      className="w-full py-4 rounded-xl font-heading font-bold text-lg uppercase tracking-wider transition-all shadow-lg 
                        bg-gradient-to-r from-[#15913f] to-[#16A34A] hover:from-[#16A34A] hover:to-[#2ecf6e] text-white disabled:opacity-50 disabled:grayscale"
                    >
                      {phase === 'betting' ? 'Place Bet' : 'Wait for Next Round'}
                    </button>
                  ) : (
                    <button 
                      onClick={handleCashout}
                      disabled={myBet.status !== 'playing' || phase !== 'playing'}
                      className={`w-full py-4 rounded-xl font-heading font-bold text-lg uppercase tracking-wider transition-all shadow-lg 
                        ${myBet.status === 'cashed_out' 
                          ? 'bg-[#16A34A]/20 text-[#2ecf6e] border border-[#16A34A]/50 cursor-not-allowed' 
                          : myBet.status === 'crashed'
                          ? 'bg-rose-500/20 text-rose-400 border border-rose-500/50 cursor-not-allowed'
                          : phase === 'betting'
                          ? 'bg-[#B059E8]/20 text-[#c98bf0] border border-[#B059E8]/50 cursor-not-allowed'
                          : 'bg-gradient-to-r from-amber-500 to-rose-500 hover:from-amber-400 hover:to-rose-400 text-white animate-pulse'}`}
                    >
                      {myBet.status === 'cashed_out' ? `Cashed Out (${myBet.cashout_at?.toFixed(2)}x)` : 
                       myBet.status === 'crashed' ? 'Crashed' : 
                       phase === 'betting' ? 'Bet Confirmed...' :
                       /* YENİ: Uçuş sırasındaki buton kazancını da formatla */
                       /* Kendi bahsi → bahsin MÜHÜRLÜ cüzdanının biriminde. */
                       `Cashout (${walletAmount(Math.floor(myBet.amount * multiplier), myBet.wallet)})`}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* 2. OYUN EKRANI */}
            <div className="order-2 md:order-none md:col-start-2 md:row-start-1 md:row-span-2 flex flex-col gap-4 min-w-0">
              
              <div className="bg-[#1A1D20] rounded-xl border border-[#2A2D31] p-3 flex items-center gap-2 overflow-x-auto scrollbar-none shadow-lg">
                <History className="w-5 h-5 text-zinc-500 mr-2 flex-shrink-0" />
                {history.length === 0 ? (
                  <span className="text-zinc-600 text-xs mm-num">Waiting for the first round...</span>
                ) : (
                  history.map((val, idx) => (
                    <div key={idx} className={`px-3 py-1 rounded-full text-xs mm-num font-bold border flex-shrink-0 ${getMultiplierColor(val)}`}>
                      {val.toFixed(2)}x
                    </div>
                  ))
                )}
              </div>

              <div className="bg-[#1A1D20] rounded-2xl border border-[#2A2D31] shadow-2xl flex-1 relative overflow-hidden flex flex-col justify-center items-center min-h-[400px]">
                
                <CrashGraph multiplier={multiplier} phase={phase} />

                <div className="relative z-10 text-center">
                  {phase === 'betting' && (
                    <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="space-y-2">
                      <Rocket className="w-16 h-16 text-zinc-500 mx-auto animate-bounce mb-4" />
                      <h2 className="text-2xl font-heading text-zinc-400 uppercase tracking-widest">Preparing for Takeoff</h2>
                      <div className="text-5xl mm-num font-bold text-white drop-shadow-lg">
                        {timeRemaining.toFixed(1)}s
                      </div>
                      <div className="h-1.5 w-48 bg-[#15171a] rounded-full mx-auto mt-6 overflow-hidden">
                         <motion.div 
                           className="h-full bg-[#B059E8]" 
                           initial={{ width: "100%" }}
                           animate={{ width: `${Math.max(0, (timeRemaining / 10) * 100)}%` }}
                           transition={{ duration: 0.1 }}
                         />
                      </div>
                    </motion.div>
                  )}

                  {phase === 'playing' && (
                    <div className="space-y-2">
                      <div className={`text-7xl md:text-9xl font-heading font-bold drop-shadow-[0_0_30px_rgba(255,255,255,0.2)] ${getMultiplierTextOnly(multiplier)}`}>
                        {multiplier.toFixed(2)}<span className="text-4xl md:text-6xl text-zinc-500 ml-2">x</span>
                      </div>
                      <p className="text-zinc-400 mm-num text-sm tracking-widest uppercase">Current Multiplier</p>
                    </div>
                  )}

                  {phase === 'crashed' && (
                    <motion.div initial={{ scale: 1.2 }} animate={{ scale: 1 }} className="space-y-2">
                      <div className="text-6xl md:text-8xl font-heading font-bold text-rose-500 drop-shadow-[0_0_30px_rgba(244,63,94,0.4)]">
                        Crash! {multiplier.toFixed(2)}x
                      </div>
                    </motion.div>
                  )}
                </div>

                <div className="absolute bottom-4 left-4 flex items-center gap-3 text-xs text-zinc-600 mm-num">
                  <div className="flex items-center gap-1">
                    <ShieldCheck className="w-4 h-4 text-[#16A34A]" />
                    <span>Provably Fair</span>
                  </div>
                  {serverHash && (
                    <span className="hidden md:inline border-l border-zinc-700 pl-3 opacity-50" title={serverHash}>
                      Hash: {serverHash.substring(0, 16)}...
                    </span>
                  )}
                </div>
              </div>

            </div>

            {/* 3. OYUNCU LİSTESİ */}
            <div className="order-3 md:order-none md:col-start-1 md:row-start-2 flex flex-col min-h-[300px]">
              <div className="bg-[#1A1D20] rounded-2xl border border-[#2A2D31] shadow-lg flex-1 flex flex-col overflow-hidden">
                <div className="bg-[#2A2D31]/30 px-4 py-3 border-b border-[#2A2D31] flex justify-between items-center">
                  <div className="flex items-center gap-2 text-zinc-400 text-sm font-bold">
                    <Users className="w-4 h-4" />
                    <span>{players.length} Players</span>
                  </div>
                  <span className="text-zinc-400 text-xs mm-num font-bold">
                    {/* YENİ: Masadaki toplam parayı formatla (Örn: Total: 1.5K $) */}
                    {/* ⚠️ CRASH MASASI KARIŞIK CÜZDANLIDIR: her bahis kasaya karşı
                        bağımsız olduğu için aynı turda hem site hem BB Coin
                        bahisleri bulunabilir. Ham toplam mili-LC ile site
                        birimini TOPLAMAK olurdu. Backend her bahse `units`
                        (site eşdeğeri) yazıyor; ortak birim odur. */}
                    Total: {formatAmount(players.reduce((sum, p) =>
                      sum + (p.units ?? (p.wallet === 'lc' ? 0 : (p.amount || 0))), 0))}<RobuxIcon size={12} className="ml-1" />
                  </span>
                </div>
                
                <div className="overflow-y-auto flex-1 p-2 space-y-1 scrollbar-thin scrollbar-thumb-zinc-700">
                  {players.length === 0 ? (
                    <div className="h-full flex items-center justify-center text-zinc-600 text-sm">No players at the table</div>
                  ) : (
                    players.map((p) => {
                      const isMe = p.user_id === user?.id;
                      return (
                        <div key={p.user_id} className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${isMe ? 'bg-[#B059E8]/10 border border-[#B059E8]/20' : 'hover:bg-[#33363b]/30'}`}>
                          <span className={`font-medium truncate max-w-[100px] ${p.status === 'crashed' ? 'text-zinc-500' : isMe ? 'text-[#c98bf0] font-bold' : 'text-zinc-300'}`}>
                            {p.username}
                          </span>
                          <div className="flex items-center gap-3">
                            {p.status === 'cashed_out' && (
                              <span className="bg-[#16A34A]/10 border border-[#16A34A]/20 text-[#2ecf6e] px-2 py-0.5 rounded text-xs mm-num">
                                {p.cashout_at?.toFixed(2)}x
                              </span>
                            )}
                            <span className={`mm-num ${p.status === 'cashed_out' ? 'text-[#2ecf6e] font-bold' : p.status === 'crashed' ? 'text-zinc-600 line-through' : 'text-zinc-400 font-bold'}`}>
                              {/* YENİ: Diğer oyuncuların koyduğu/kazandığı paraları da formatla */}
                              {p.status === 'cashed_out' ? formatAmount(Math.floor(p.amount * p.cashout_at)) : formatAmount(p.amount)}
                            </span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

          </main>
        </div>
      </div>
    </div>
  );
};